import { createLogger } from '../utils/logger';

import http from './http';


const logger = createLogger('letterToLovedOnes');

export interface LetterRecipientRec {
  id?: string;
  name: string;
  relationship?: string;
}

export interface LetterToLovedOnesRec {
  id: string;
  userId: string;
  letterContent?: string;
  recipientName?: string;
  recipientRelationship?: string;
  title?: string;
  occasion?: string;
  status?: string; // 'draft' while composing, 'saved' once committed
  salutation?: string;
  recipients?: LetterRecipientRec[];
  deliveryTrigger?: 'eol' | 'manual' | 'date' | null;
  releaseDelayHours?: number;
  releaseAt?: string | null;
  isLocked?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface DeliveryCandidate {
  email: string;
  firstName?: string;
  lastName?: string;
  status?: string;
  visibilityId?: string;
}

export interface DeliveryCandidates {
  successors: DeliveryCandidate[];
  keyholders: DeliveryCandidate[];
}

class LetterToLovedOnesService {
  /**
   * The owner's letters, or, with `targetUserId`, the letters another
   * user (the owner) has released to the caller as their keyholder or
   * successor. The server checks the relationship, the grants and any
   * release delay. Until 2026-10-23 the target was dropped here, so a
   * keyholder's Letter to Loved Ones screen asked for their own letters
   * and always read "No letter to loved ones found" (step 3, third run).
   */
  async getAllLettersToLovedOnes(targetUserId?: string): Promise<LetterToLovedOnesRec[]> {
    const response = await http.get('/api/letter-to-loved-ones', {
      params: targetUserId ? { targetUserId } : undefined,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch letters to loved ones');
    }

    return response.data.data || [];
  }

  async getLetterToLovedOnesById(id: string, targetUserId?: string): Promise<LetterToLovedOnesRec | null> {
    // GET /:id is the owner's own record only. Someone viewing another
    // user's letter reads it from the filtered list instead, so the
    // server's grant and release-delay checks still apply.
    if (targetUserId) {
      try {
        const letters = await this.getAllLettersToLovedOnes(targetUserId);
        return letters.find((l) => l.id === id) ?? null;
      } catch (error) {
        logger.error('Error fetching a shared letter to loved ones by ID:', error);
        return null;
      }
    }

    try {
      const response = await http.get(`/api/letter-to-loved-ones/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch letter to loved ones');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching letter to loved ones by ID:', error);
      return null;
    }
  }

  async createLetterToLovedOnes(letterData: Partial<LetterToLovedOnesRec>): Promise<LetterToLovedOnesRec> {
    const response = await http.post('/api/letter-to-loved-ones', letterData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save letter to loved ones');
    }

    return response.data.data;
  }

  async updateLetterToLovedOnes(id: string, letterData: Partial<LetterToLovedOnesRec>): Promise<LetterToLovedOnesRec> {
    const response = await http.patch(`/api/letter-to-loved-ones/${id}`, letterData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update letter to loved ones');
    }

    return response.data.data;
  }

  async deleteLetterToLovedOnes(id: string): Promise<void> {
    const response = await http.delete(`/api/letter-to-loved-ones/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete letter to loved ones');
    }
  }

  async lockLetterToLovedOnes(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for letter to loved ones ID:', id);
    const response = await http.post(`/api/letter-to-loved-ones/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock letter to loved ones');
    }
  }

  async unlockLetterToLovedOnes(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service Debug] Calling unlock API for letter to loved ones ID:', id, 'granteeEmails:', granteeEmails);
    const response = await http.post(`/api/letter-to-loved-ones/${id}/unlock`, { granteeEmails });
    logger.debug('[Service Debug] Unlock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock letter to loved ones');
    }
  }

  async getLetterToLovedOnesAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/letter-to-loved-ones/${id}/access-grants`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch letter to loved ones access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching letter to loved ones access grants:', error);
      return [];
    }
  }

  async deleteLetterToLovedOnesAccessGrant(id: string, grantId: string): Promise<void> {
    const response = await http.delete(`/api/letter-to-loved-ones/${id}/access-grants/${grantId}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete access grant');
    }
  }

  /**
   * Orion's interview answers (letter-first flow). Stored per user so a
   * second letter to a different recipient starts from what they already
   * said instead of re-asking everything.
   */
  async getInterviewAnswers(): Promise<Record<string, string>> {
    try {
      const response = await http.get('/api/letter-to-loved-ones/interview-answers');
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch interview answers');
      }
      return response.data.data || {};
    } catch (error) {
      logger.error('Error fetching interview answers:', error);
      return {};
    }
  }

  async saveInterviewAnswers(answers: Record<string, string>): Promise<void> {
    const response = await http.put('/api/letter-to-loved-ones/interview-answers', { answers });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save interview answers');
    }
  }

  /**
   * Delivery (letter tier+): who can receive a letter, and per-letter
   * trigger configuration. The backend refuses the free tier, so the
   * paywall is enforced server-side too.
   */
  async getDeliveryCandidates(): Promise<DeliveryCandidates> {
    const response = await http.get('/api/letter-to-loved-ones/delivery-candidates');
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch delivery candidates');
    }
    return response.data.data || { successors: [], keyholders: [] };
  }

  /**
   * Delivery grants for ALL letters, grouped by letter id. Shows "who
   * receives this" on the letters home screen: death-event-only grants
   * (waiting for release) and living can-view grants (date trigger) both
   * count; revoked ones don't.
   */
  async getDeliveryGrantsByLetter(): Promise<Record<string, string[]>> {
    const response = await http.get('/api/access-grants/task/letter_to_loved_ones');
    const grants: {
      entityId: string;
      grantedToEmail: string;
      status?: string;
      canView?: boolean;
      isDeathEventOnly?: boolean;
    }[] = response.data?.grants ?? [];
    const byLetter: Record<string, string[]> = {};
    for (const grant of grants) {
      if (grant.status === 'revoked') continue;
      if (!grant.canView && !grant.isDeathEventOnly) continue;
      if (!byLetter[grant.entityId]) byLetter[grant.entityId] = [];
      if (!byLetter[grant.entityId].includes(grant.grantedToEmail)) {
        byLetter[grant.entityId].push(grant.grantedToEmail);
      }
    }
    return byLetter;
  }

  async setDelivery(
    id: string,
    settings: {
      trigger: 'eol' | 'manual' | 'date';
      releaseDelayHours?: number;
      releaseAt?: string;
      granteeEmails?: string[];
    },
  ): Promise<LetterToLovedOnesRec> {
    const response = await http.put(`/api/letter-to-loved-ones/${id}/delivery`, settings);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save delivery settings');
    }
    return response.data.data;
  }
}

export const letterToLovedOnesService = new LetterToLovedOnesService();